import { createProxy, isChanged, getUntracked, trackMemo } from 'proxy-compare';
import { AnySelector, DropFirstParameter, GetStoreFn, ModuleStore } from '../types';
import { createLruCache, createSingletonCache } from './caches';
import { addCurrentSelectorStore } from './createSubscribableSelector';
import { getStateForSelect } from '../serverSnapshot';
import { canBeProxy, compareArg, isDraftRecursive } from './memoizeUtils';

/**
 * Данные Proxy cache
 */
type ProxyCacheData = {
    /** Cтор, используемый в селекторе (либо напрямую либо через вложенные селекторы) */
    store: ModuleStore;
    /** Стейт стора на момент последнего вызова */
    state: object;
    /** WeakMap содержащий список используемых полей стейта */
    affected: WeakMap<object, unknown>;
    /** Функция для получения части стейта стора, передаваемой в селектор */
    getSelectorState: (state: object) => unknown;
};

/**
 * Кеш для селектора
 */
type CacheEntry = {
    /**
     * Зависимости селектора
     */
    deps: ProxyCacheData[];
    /**
     * Результат вычисления селектора
     */
    result: unknown;
};

/** Колстэк в который записываются все вложено вызванные селекторы */
const SELECTORS_CALL_STACK: AnySelector[] = [];

/** Хранилище в которое сохраняется ProxyCacheData всех вложено вызванных селекторов  */
const ALL_SELECTORS_PROXY_CACHE_DATA: Map<AnySelector, ProxyCacheData[]> = new Map();

/**
 * Функция для записи данных о селекторе в глобальные переменные определенные выше
 * Выполняется до вызова селектора
 *
 * @param selectorFn - Текущий вызванный селектор
 * @param data - ProxyCacheData для текущего селектора
 * @internal
 */
const writeSelectorDataToGlobalScope = (selectorFn: AnySelector, data: ProxyCacheData[]) => {
    SELECTORS_CALL_STACK.push(selectorFn);
    addDependenciesToSelectorsInStack(data);
};

/**
 * Записывает данные о текущем селекторе во все селекторы в колстэке.
 *
 * @param data - ProxyCacheData для текущего селектора
 * @internal
 */
const addDependenciesToSelectorsInStack = (data: ProxyCacheData[]) => {
    for (const fn of SELECTORS_CALL_STACK) {
        const SELECTOR_PROXY_CACHE_DATA = ALL_SELECTORS_PROXY_CACHE_DATA.get(fn);

        if (SELECTOR_PROXY_CACHE_DATA) {
            SELECTOR_PROXY_CACHE_DATA.push(...data);
        } else {
            ALL_SELECTORS_PROXY_CACHE_DATA.set(fn, data);
        }
    }
};

/**
 * Функция для чтения ProxyCache данных селектора из глобальных переменных определенных выше
 * Выполняется после вызова селектора для записи необходимых данных в кэш селектора
 *
 * @param selectorFn - Текущий вызванный селектор
 * @returns Список ProxyCacheData текущего селектора и всех селекторов, которые были вложено вызваны
 * @internal
 */
const getSelectorProxyCacheDataFromGlobalScope = (selectorFn: AnySelector) => {
    return ALL_SELECTORS_PROXY_CACHE_DATA.get(selectorFn);
};

/**
 * Функция очистки данных о селекторе из глобальных переменных.
 * Выполняется после вызова селектора и после записи необходимых данных в кэш селектора
 *
 * @param selectorFn - Текущий вызванный селектор
 * @internal
 */
const clearSelectorDataFromGlobalScope = (selectorFn: AnySelector) => {
    SELECTORS_CALL_STACK.pop();
    ALL_SELECTORS_PROXY_CACHE_DATA.delete(selectorFn);
};

/**
 * Параметры создания функции мемоизации
 */
interface MemoizeAutotrackSelectorBuilderOptions<
    StoreState extends object,
    SelectorState extends unknown = StoreState,
> {
    /** Функция получения стора */
    getStore: GetStoreFn;
    /**
     * Функция для получения части стейта стора, передаваемой в селектор
     * (по дефолту используется весь стейт)
     */
    getSelectorState?: (storeState: StoreState) => SelectorState;
}

/**
 * Параметры мемоизации селектора
 * @external
 */
export interface MemoizeAutotrackSelectorOptions {
    /**
     * Размер LRU кэша (для селекторов с параметрами)
     * @default 50
     */
    lruCacheSize?: number;
}

/**
 * Параметры мемоизации селектора
 * @internal
 */
interface MemoizeAutotrackSelectorInternalOptions extends MemoizeAutotrackSelectorOptions {
    /**
     * Количество дополнительных параметров селектора, которые должны участвовать в мемоизации.
     * Передается в явном виде, так как из-за rest args информация теряется в обертках селектора.
     */
    restArgsCount?: number;
}

/**
 * Функция для рекурсивного снятия отслеживания
 *
 * @param x - Переменная, которая возможно отслеживается
 * @param selectorState - Стейт, который селектор получает первым аргументом
 * @param affected - WeakMap содержащая обращения к selectorState
 * @param seen - Set для записи уже посещенных объектов. Может быть полезно для рекурсивных структур
 * @returns Переданную переменную без proxy
 */
const untrack = <T>(
    x: T,
    selectorState: unknown,
    affected: WeakMap<object, unknown>,
    seen: WeakSet<object> = new WeakSet()
): T => {
    // Только объекты и массивы могут быть отслеживаемыми
    if (typeof x !== 'object' || x === null) return x;

    const untrackedObj = getUntracked(x);

    // getUntracked возвращает `null`, если переменная не отслеживалась
    // Поэтому если вернул не `null`, то значит что это изначальный объект
    if (untrackedObj !== null) {
        // Помечаем весь возвращаемый объект как affected
        // для случаев, когда в селекторе используются вложенные поля,
        // а возвращается объект целиком, например `memoize(state => state.find(({ id }) => id == 1)`
        // trackMemo отрезает все обращения к вложенным полям возвращаемого объекта
        trackMemo(x);

        // Если селектор возвращает стейт целиком
        // Например, `memoize(state => state)`, `memoize(state => ({ state }))` и тд
        // То устанавливаем в affected 0 (подходит любое falsy значение), чтобы объект считался использованным (см. hasSelectorDepsChanged)
        // И любое его изменение приводило к перерасчету селектора
        if (selectorState === untrackedObj) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            affected.set(selectorState as object, 0);
        }

        return untrackedObj;
    }

    if (!seen.has(x)) {
        seen.add(x);
        const entries = Object.entries(x);
        for (const [k, v] of entries) {
            const vv = untrack(v, selectorState, affected, seen);

            if (!Object.is(vv, v)) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                x[k as keyof T] = vv;
            }
        }
    }

    return x;
};

/**
 * Билдер для создания функции Autotrack мемоизации модульных селекторов
 *
 * @typeParam StoreState - Тип стейта стора
 * @returns Функция Autotrack мемоизации модульных селекторов
 */
export function memoizeAutotrackSelectorBuilder<StoreState extends object, SelectorState extends unknown = StoreState>({
    getStore,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    getSelectorState = (storeState: StoreState) => storeState as unknown as SelectorState,
}: MemoizeAutotrackSelectorBuilderOptions<StoreState, SelectorState>) {
    return function memoizeAutotrackSelector<Selector extends AnySelector<SelectorState>>(
        selectorFn: Selector,
        { lruCacheSize = 50, restArgsCount }: MemoizeAutotrackSelectorInternalOptions = {}
    ) {
        const singletonCache = createSingletonCache<CacheEntry>(selectorFn, { compareArgFn: compareArg });
        const lruCache = createLruCache<CacheEntry>(selectorFn, lruCacheSize, { compareArgFn: compareArg });

        const targetCache = new WeakMap<object, unknown>();

        // Сохраняем ссылку на пустой массив элементов, чтобы не делать для него shallowEquals на каждый вызов
        const emptyArgs: unknown[] = [];

        return function runSelector(
            storeState: StoreState,
            ...args: Parameters<DropFirstParameter<Selector>>
        ): ReturnType<Selector> {
            // `restArgsCount` вычисляется через `selector.length` и учитывает только явно объявленные
            // параметры до `...rest`. Если в вызове фактически пришло больше аргументов, то селектор
            // использует `...rest`, и для корректного ключа мемоизации нужно учесть весь хвост.
            const argsCount = restArgsCount !== undefined ? Math.max(restArgsCount, args.length) : args.length;

            // Для селекторов с аргументами используем LRU cache, а без - Singleton
            const cache = argsCount ? lruCache : singletonCache;

            // Используем список аргументов в качестве ключа кэширования
            const cacheKey = argsCount ? args.slice(0, argsCount) : emptyArgs;

            const cacheData = cache.get(cacheKey)?.value;
            if (cacheData) {
                const deps = cacheData.deps;

                if (!hasSelectorDepsChanged(deps)) {
                    // Записываем селектор в зависимости вышестоящих селекторов, в которых он вызывается
                    addDependenciesToSelectorsInStack(deps);

                    // Добавляем все сторы в глобальную переменную чтобы потом сделать подписку
                    for (const item of deps) {
                        addCurrentSelectorStore(item.store);
                    }

                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                    return cacheData.result as ReturnType<Selector>;
                }
            }

            try {
                const store = getStore();
                const affected = new WeakMap<object, unknown>();

                // Записываем данные о селекторе в call stack, чтобы посчитать зависимости с учетом вложенных селекторов
                writeSelectorDataToGlobalScope(selectorFn, [
                    {
                        store,
                        state: storeState,
                        affected,
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                        getSelectorState: getSelectorState as ProxyCacheData['getSelectorState'],
                    },
                ]);

                const state = getSelectorState(storeState);
                const stateCanBeProxy = canBeProxy(state);
                const stateArg = stateCanBeProxy ? createProxy(state, affected, undefined, targetCache) : state;

                const resultMaybeProxy = selectorFn(stateArg, ...args);

                // Так как `createProxy` при обращении к вложенному объекту также оборачивает его в proxy
                // То может получиться, что мы вернем из селектора proxy объект
                // И дальнейшее использование этого объекта (например, в компоненте)
                // Может привести к некорректной работе мемоизации
                // Поэтому с помощью функции `untrack` рекурсивно убираем proxy с результата
                const result = untrack(resultMaybeProxy, state, affected);

                // После того как селектор был вызван и вернул результат,
                // В глобальных переменных есть информация о всех вызванных вложено селекторах
                const deps = getSelectorProxyCacheDataFromGlobalScope(selectorFn);

                // Пропускаем сохранение результата в кэш, в случае если среди параметров селектора есть draft
                // Например если селектор вызвали из редьюсера и прокинули часть стейта
                const skipCache = cacheKey.some(arg => isDraftRecursive(arg));
                if (!skipCache) {
                    cache.set(cacheKey, {
                        // убираем undefined из типов, так как селектор гарантировано лежит в стеке
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                        deps: deps as NonNullable<typeof deps>,
                        result,
                    });
                }

                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                return result as ReturnType<Selector>;
            } finally {
                // После возвращения результата либо ошибки
                // Очищаем данные о селекторе из глобальных переменных
                clearSelectorDataFromGlobalScope(selectorFn);
            }
        };
    };
}

/**
 * Проверяем изменились ли используемые поля во всех сторах селектора
 * @param deps Зависимости селектора
 * @returns true, если есть изменения хотя бы в одной из зависимостей
 */
function hasSelectorDepsChanged<StoreState extends object>(deps: ProxyCacheData[]) {
    for (const dep of deps) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        const previousState = dep.getSelectorState(dep.state as StoreState);
        // При гидрации селектор считается над серверным снепшотом, поэтому и кеш сверяем с ним,
        // иначе закешированное клиентское значение будет отдано вместо серверного.
        const currentState = dep.getSelectorState(getStateForSelect(dep.store));

        // Случай если в стейте слайса хранится примитив
        if (!canBeProxy(previousState) || !canBeProxy(currentState)) {
            if (previousState !== currentState) {
                return true;
            }

            continue;
        }

        // Проверяем был ли вообще стейт использован в селекторе,
        // Так как isChanged возвращает true, если селектор не использовал поля стейта
        const used = dep.affected.has(previousState);

        if (
            used &&
            isChanged(
                previousState,
                currentState,
                dep.affected,
                // Этот кеш дает небольшое замедление, но защищает от infinite loop
                // в случае циклических ссылок внутри стейта
                new WeakMap()
            )
        ) {
            return true;
        }
    }

    return false;
}
